import React, { useRef, useState } from 'react';
import { useAlert } from 'react-alert';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import I18n from 'i18n-js';

import { submitCollection } from '@lootboy/lib/slices/cardCollections/actions';
import useProgressBar from '@lootboy/lib/slices/cardCollections/hooks/useProgressBar';

import Alert from '~/components/Alert';
import StyledButton, {
  EStyledButtonVariant,
} from '~/components/atoms/StyledButton';
import BottomSheetDialog, {
  TBottomSheetDialogElement,
} from '~/components/molecules/BottomSheetDialog';
import SubmitCollectionProgressBar from '~/components/molecules/SubmitCollectionProgressBar';

import { useAppDispatch } from '~/redux/store/useAppDispatch';

import styles from './SubmitCollectionDialog.module.scss';

interface ISubmitCollectionDialogProps {
  onClose: () => void;
  collectionId: string;
  showCollectionProgress: boolean;
}

const SubmitCollectionDialog: React.FC<ISubmitCollectionDialogProps> = ({
  collectionId,
  onClose,
  showCollectionProgress,
}) => {
  const alert = useAlert();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const dialogElement = useRef<TBottomSheetDialogElement>(null);
  const progress = useProgressBar(collectionId);
  const [isSubmitClicked, setSubmitClicked] = useState(false);

  const onCancel = () => {
    if (dialogElement.current) {
      dialogElement.current.close();
    }
  };

  const onSubmit = async () => {
    try {
      setSubmitClicked(true);
      const result = await dispatch(submitCollection(collectionId));
      if (result?.purchases?.[0]) {
        history.push(`/purchases/${result.purchases[0]._id}`);
      }
    } catch (e) {
      alert.error(
        <Alert
          body={(e as { message: string })?.message || ''}
          title={I18n.t('common_error')}
        />,
      );
      setSubmitClicked(false);
    }
  };

  return (
    <BottomSheetDialog
      ref={dialogElement}
      onClose={onClose}
      header={progress.dialogHeader || ''}
    >
      <div className={styles.container}>
        <h3 className={styles.header}>
          {I18n.t('collections_bs_submit_headline')}
        </h3>
        {showCollectionProgress && (
          <SubmitCollectionProgressBar
            value={progress.value}
            target={progress.target}
            targetMax={progress.maxTarget}
          />
        )}
        <p
          className={classNames(
            styles.submitInfo,
            !showCollectionProgress && styles.noProgress,
          )}
        >
          {I18n.t('collections_bs_submit_sub')}
        </p>
      </div>
      <div className={styles.actions}>
        <StyledButton
          variant={EStyledButtonVariant.GRAY}
          onClick={onCancel}
        >
          {I18n.t('common_later')}
        </StyledButton>
        <StyledButton
          variant={EStyledButtonVariant.BLUE}
          onClick={onSubmit}
          disabled={isSubmitClicked}
        >
          {I18n.t('collections_bs_submit_pbutton')}
        </StyledButton>
      </div>
    </BottomSheetDialog>
  );
};

export default SubmitCollectionDialog;
